// Inline editor opened by a row's [Edit] action. Term is read-only per
// the locked decision in plan/02_review_ui.md — renaming would break the
// source-span anchor.

import { useState } from "react";
import type { GlossaryEntry, TermKind } from "../data/types";
import { useReviewStore } from "./store";

interface Props {
  entry: GlossaryEntry;
  currentDefinition: string;
  currentKind: TermKind;
  currentAlternates: string[];
  onClose: () => void;
}

const KINDS: TermKind[] = ["concept", "acronym", "type", "error", "param", "resource", "other"];

export function ReviewEditor({ entry, currentDefinition, currentKind, currentAlternates, onClose }: Props) {
  const [def, setDef] = useState(currentDefinition);
  const [kind, setKind] = useState<TermKind>(currentKind);
  const [altInput, setAltInput] = useState("");
  const [alts, setAlts] = useState<string[]>(currentAlternates);
  const edit = useReviewStore((s) => s.edit);

  const addAlt = () => {
    const v = altInput.trim();
    if (!v) return;
    if (alts.includes(v)) { setAltInput(""); return; }
    setAlts([...alts, v]);
    setAltInput("");
  };
  const removeAlt = (v: string) => setAlts(alts.filter((a) => a !== v));

  const save = () => {
    edit(entry.term_id, { definition: def, kind, alternates: alts });
    onClose();
  };

  return (
    <div className="maisight-review-editor" data-testid={`editor-${entry.term_id}`}>
      <label className="maisight-review-field">
        <span className="maisight-review-field-label">Term</span>
        <input className="maisight-review-input" value={entry.term} readOnly aria-readonly="true" />
      </label>
      <label className="maisight-review-field">
        <span className="maisight-review-field-label">Definition</span>
        <textarea
          className="maisight-review-textarea"
          value={def}
          onChange={(e) => setDef(e.target.value)}
          rows={3}
          aria-label="Definition"
        />
      </label>
      <label className="maisight-review-field">
        <span className="maisight-review-field-label">Kind</span>
        <select
          className="maisight-review-input"
          value={kind}
          onChange={(e) => setKind(e.target.value as TermKind)}
          aria-label="Kind"
        >
          {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
        </select>
      </label>
      <div className="maisight-review-field">
        <span className="maisight-review-field-label">Alternates</span>
        <div className="maisight-review-chips">
          {alts.map((a) => (
            <button
              key={a}
              type="button"
              className="maisight-review-chip"
              onClick={() => removeAlt(a)}
              aria-label={`Remove alternate ${a}`}
            >
              {a} ×
            </button>
          ))}
          <input
            className="maisight-review-input maisight-review-input--inline"
            value={altInput}
            onChange={(e) => setAltInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); addAlt(); }
            }}
            placeholder="Add alternate spelling…"
            aria-label="Add alternate"
          />
        </div>
      </div>
      <div className="maisight-review-editor-actions">
        <button type="button" className="maisight-review-btn maisight-review-btn--ghost" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="maisight-review-btn maisight-review-btn--primary" onClick={save}>
          Save edit
        </button>
      </div>
    </div>
  );
}
